import { workoutOverridesSchema, type WorkoutOverride } from '@/domain/health/workoutAppearance'
import { intakeSchema, savedFoodSchema, validateNutritionSnapshot, type IntakeRecord, type SavedFood } from '@/domain/health/nutrition'
import { moodRecordSchema, periodRecordSchema, validateJournalSnapshot, type MoodRecord, type PeriodRecord } from '@/domain/health/journal'
import { z } from 'zod'

import type {
  AppSettings,
  MetricRecord,
  SleepRecord,
  WorkoutRecord,
} from '@/domain/health/types'
import {
  durationMinutes,
  localDayAt,
  parseIsoInstant,
} from '@/domain/health/time'

export interface BackupPayloadV1 {
  workoutOverrides?: WorkoutOverride[]
  schemaVersion: 1
  exportedAt: string
  metrics: MetricRecord[]
  sleeps: SleepRecord[]
  workouts: WorkoutRecord[]
  intakes?: IntakeRecord[]
  savedFoods?: SavedFood[]
  moods?: MoodRecord[]
  periods?: PeriodRecord[]
  settings: AppSettings
}

export interface BackupEnvelopeV1 {
  formatVersion: 1
  kdf: {
    name: 'PBKDF2'
    hash: 'SHA-256'
    iterations: number
    saltBase64: string
  }
  cipher: {
    name: 'AES-GCM'
    ivBase64: string
  }
  ciphertextBase64: string
}

export const BACKUP_V1_MAX_ITERATIONS = 600_000
export const BACKUP_V1_MIN_ITERATIONS = 1_000
export const BACKUP_V1_MAX_CIPHERTEXT_BASE64_LENGTH = 10 * 1024 * 1024

const instantSchema = z.string().refine((value) => {
  try {
    return parseIsoInstant(value).toISOString() === value
  } catch {
    return false
  }
}, 'instant must be a canonical ISO string')
const localDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u)
const sourceSchema = z.literal('manual')
const commonRecordFields = {
  id: z.string().uuid(),
  source: sourceSchema,
  createdAt: instantSchema,
  updatedAt: instantSchema,
}

const metricSchema = z.discriminatedUnion('type', [
  z.strictObject({
    ...commonRecordFields,
    type: z.literal('weight'),
    value: z.number().finite().positive().max(500),
    unit: z.literal('kg'),
    recordedAt: instantSchema,
    timeZone: z.string().min(1),
    localDay: localDaySchema,
  }),
  z.strictObject({
    ...commonRecordFields,
    type: z.literal('bodyFat'),
    value: z.number().finite().positive().max(100),
    unit: z.literal('percent'),
    recordedAt: instantSchema,
    timeZone: z.string().min(1),
    localDay: localDaySchema,
  }),
  z.strictObject({
    ...commonRecordFields,
    type: z.literal('activeEnergy'),
    value: z.number().finite().min(0).max(20_000),
    unit: z.literal('kcal'),
    recordedAt: instantSchema,
    timeZone: z.string().min(1),
    localDay: localDaySchema,
  }),
])

const sleepSchema = z
  .strictObject({
    ...commonRecordFields,
    startAt: instantSchema,
    endAt: instantSchema,
    durationMinutes: z.number().finite().min(1).max(1_440),
    wakeTimeZone: z.string().min(1),
    wakeLocalDay: localDaySchema,
  })
  .refine((record) => Date.parse(record.endAt) > Date.parse(record.startAt))

const workoutSchema = z.strictObject({
  ...commonRecordFields,
  title: z.string().trim().min(1).refine((value) => [...value].length <= 40).optional(),
  status: z.literal('planned').optional(),
  activityType: z.enum([
    'strength',
    'running',
    'walking',
    'cycling',
    'swimming',
    'yoga',
    'other',
  ]),
  startedAt: instantSchema,
  timeZone: z.string().min(1),
  localDay: localDaySchema,
  durationMinutes: z.number().finite().min(1).max(1_440),
  activeEnergyKcal: z.number().finite().min(0).max(20_000).optional(),
  notes: z
    .string()
    .refine((value) => Array.from(value).length <= 1_000)
    .optional(),
})

const settingsSchema = z.strictObject({
  displayWeightUnit: z.enum(['kg', 'lb']),
  schemaVersion: z.literal(1),
})

export const backupPayloadV1Schema: z.ZodType<BackupPayloadV1> = z
  .strictObject({
    schemaVersion: z.literal(1),
    exportedAt: instantSchema,
    metrics: z.array(metricSchema),
    sleeps: z.array(sleepSchema),
    workouts: z.array(workoutSchema),
    settings: settingsSchema,
    workoutOverrides: workoutOverridesSchema.optional(),
    intakes: z.array(intakeSchema).optional(),
    savedFoods: z.array(savedFoodSchema).optional(),
    moods: z.array(moodRecordSchema).optional(),
    periods: z.array(periodRecordSchema).optional(),
  })
  .superRefine((payload, context) => {
    try { validateNutritionSnapshot(payload) } catch {
      context.addIssue({ code: 'custom', message: 'invalid or duplicate nutrition record' })
    }
    try { validateJournalSnapshot(payload) } catch {
      context.addIssue({ code: 'custom', message: 'invalid or duplicate journal day' })
    }
    const validateUniqueIds = (
      records: ReadonlyArray<{ id: string }>,
      table: 'metrics' | 'sleeps' | 'workouts',
    ) => {
      const ids = new Set<string>()
      records.forEach((record, index) => {
        if (ids.has(record.id)) {
          context.addIssue({
            code: 'custom',
            message: 'duplicate record id',
            path: [table, index, 'id'],
          })
        }
        ids.add(record.id)
      })
    }

    validateUniqueIds(payload.metrics, 'metrics')
    validateUniqueIds(payload.sleeps, 'sleeps')
    validateUniqueIds(payload.workouts, 'workouts')

    const activeEnergyDays = new Set<string>()
    payload.metrics.forEach((record, index) => {
      try {
        if (localDayAt(record.recordedAt, record.timeZone) !== record.localDay) {
          context.addIssue({
            code: 'custom',
            message: 'metric local day does not match its instant and time zone',
            path: ['metrics', index, 'localDay'],
          })
        }
      } catch {
        context.addIssue({
          code: 'custom',
          message: 'metric time zone is invalid',
          path: ['metrics', index, 'timeZone'],
        })
      }

      if (record.type !== 'activeEnergy') return
      if (activeEnergyDays.has(record.localDay)) {
        context.addIssue({
          code: 'custom',
          message: 'duplicate manual active energy day',
          path: ['metrics', index, 'localDay'],
        })
      }
      activeEnergyDays.add(record.localDay)
    })

    const wakeDays = new Set<string>()
    payload.sleeps.forEach((record, index) => {
      try {
        if (localDayAt(record.endAt, record.wakeTimeZone) !== record.wakeLocalDay) {
          context.addIssue({
            code: 'custom',
            message: 'sleep wake day does not match its end instant and time zone',
            path: ['sleeps', index, 'wakeLocalDay'],
          })
        }
      } catch {
        context.addIssue({
          code: 'custom',
          message: 'sleep wake time zone is invalid',
          path: ['sleeps', index, 'wakeTimeZone'],
        })
      }

      if (durationMinutes(record.startAt, record.endAt) !== record.durationMinutes) {
        context.addIssue({
          code: 'custom',
          message: 'sleep duration does not match its instants',
          path: ['sleeps', index, 'durationMinutes'],
        })
      }

      if (wakeDays.has(record.wakeLocalDay)) {
        context.addIssue({
          code: 'custom',
          message: 'duplicate manual sleep wake day',
          path: ['sleeps', index, 'wakeLocalDay'],
        })
      }
      wakeDays.add(record.wakeLocalDay)
    })

    const sleepsByStart = [...payload.sleeps].sort(
      (left, right) =>
        parseIsoInstant(left.startAt).getTime() - parseIsoInstant(right.startAt).getTime(),
    )
    let latestEnd = Number.NEGATIVE_INFINITY
    sleepsByStart.forEach((record) => {
      const start = parseIsoInstant(record.startAt).getTime()
      const end = parseIsoInstant(record.endAt).getTime()
      if (start < latestEnd) {
        context.addIssue({
          code: 'custom',
          message: 'sleep records must not overlap',
          path: ['sleeps'],
        })
      }
      latestEnd = Math.max(latestEnd, end)
    })

    payload.workouts.forEach((record, index) => {
      try {
        if (localDayAt(record.startedAt, record.timeZone) !== record.localDay) {
          context.addIssue({
            code: 'custom',
            message: 'workout local day does not match its instant and time zone',
            path: ['workouts', index, 'localDay'],
          })
        }
      } catch {
        context.addIssue({
          code: 'custom',
          message: 'workout time zone is invalid',
          path: ['workouts', index, 'timeZone'],
        })
      }
    })
  })

export const backupEnvelopeV1Schema: z.ZodType<BackupEnvelopeV1> = z.strictObject({
  formatVersion: z.literal(1),
  kdf: z.strictObject({
    name: z.literal('PBKDF2'),
    hash: z.literal('SHA-256'),
    iterations: z
      .number()
      .int()
      .min(BACKUP_V1_MIN_ITERATIONS)
      .max(BACKUP_V1_MAX_ITERATIONS),
    saltBase64: z.string().length(24),
  }),
  cipher: z.strictObject({
    name: z.literal('AES-GCM'),
    ivBase64: z.string().length(16),
  }),
  ciphertextBase64: z
    .string()
    .min(24)
    .max(BACKUP_V1_MAX_CIPHERTEXT_BASE64_LENGTH),
})
